import { useMemo, useState, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, getElementAtEvent } from "react-chartjs-2";
import { TimeSeriesData } from "../types/statistics.types";
import { X, ArrowSquareOut, Plus, CheckCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface VolumeTimeSeriesChartProps {
  data: TimeSeriesData | null;
  loading: boolean;
}

interface SelectedPeriodData {
  periodLabel: string;
  period: string;
  created: number;
  completed: number;
  createdTicketIds: number[];
  completedTicketIds: number[];
}

export function VolumeTimeSeriesChart({ data, loading }: VolumeTimeSeriesChartProps) {
  const chartRef = useRef<any>(null);
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState<SelectedPeriodData | null>(null);

  const chartData = useMemo(() => {
    if (!data?.data) return null;

    return {
      labels: data.data.map((d) => d.periodLabel),
      datasets: [
        {
          label: "Nieuw",
          data: data.data.map((d) => d.created),
          borderColor: "#EA8162",
          backgroundColor: "rgba(234, 129, 98, 0.1)",
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 8,
          pointBackgroundColor: "#EA8162",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
        },
        {
          label: "Opgelost",
          data: data.data.map((d) => d.completed),
          borderColor: "#5B7304",
          backgroundColor: "rgba(91, 115, 4, 0.1)",
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 8,
          pointBackgroundColor: "#5B7304",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
        },
      ],
    };
  }, [data]);

  const handleChartClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!chartRef.current || !data?.data) return;

    const elements = getElementAtEvent(chartRef.current, event);
    if (elements.length > 0) {
      const index = elements[0].index;
      const dataPoint = data.data[index];

      setSelectedPeriod({
        periodLabel: dataPoint.periodLabel,
        period: dataPoint.period,
        created: dataPoint.created,
        completed: dataPoint.completed,
        createdTicketIds: dataPoint.createdTicketIds || [],
        completedTicketIds: dataPoint.completedTicketIds || [],
      });
    }
  }, [data]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index" as const,
        intersect: false,
      },
      onClick: undefined, // Handled by react-chartjs-2's getElementAtEvent
      plugins: {
        legend: {
          position: "top" as const,
          labels: {
            usePointStyle: true,
            padding: 20,
          },
        },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: "#EA8162",
          borderWidth: 1,
          padding: 12,
          callbacks: {
            afterBody: (context: any) => {
              const idx = context[0]?.dataIndex;
              if (idx !== undefined && data?.data[idx]) {
                const point = data.data[idx];
                const netChange = point.created - point.completed;
                return [
                  "",
                  `Netto wijziging: ${netChange > 0 ? "+" : ""}${netChange}`,
                  "",
                  "Klik voor details",
                ];
              }
              return [];
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(49, 64, 59, 0.1)",
          },
          ticks: {
            stepSize: 5,
          },
        },
      },
    }),
    [data]
  );

  const openTicketInNewTab = (ticketId: number) => {
    window.open(`/helpdesk/${ticketId}`, '_blank');
  };

  const openTicketsInHelpdeskView = (ticketIds: number[]) => {
    if (ticketIds.length === 0) return;
    // Navigate to helpdesk with filter for these specific tickets
    const ticketIdsParam = ticketIds.join(',');
    navigate(`/helpdesk?ticketIds=${ticketIdsParam}`);
  };

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg">Ticket Volume</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Ticket Volume</CardTitle>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div>
              <span className="font-semibold text-[#EA8162]">
                {data?.totals.created ?? 0}
              </span>{" "}
              nieuw
            </div>
            <div>
              <span className="font-semibold text-[#5B7304]">
                {data?.totals.completed ?? 0}
              </span>{" "}
              opgelost
            </div>
            <div
              className={`font-semibold ${
                (data?.totals.netChange ?? 0) > 0
                  ? "text-red-500"
                  : (data?.totals.netChange ?? 0) < 0
                  ? "text-green-500"
                  : "text-neutral-500"
              }`}
            >
              {(data?.totals.netChange ?? 0) > 0 ? "+" : ""}
              {data?.totals.netChange ?? 0} netto
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Klik op een datapunt voor details
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          {chartData && (
            <Line
              ref={chartRef}
              data={chartData}
              options={chartOptions}
              onClick={handleChartClick}
            />
          )}
        </div>

        {/* Selected Period Detail Panel */}
        {selectedPeriod && (
          <div className="mt-4 border rounded-lg p-4 bg-muted/20">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">
                Details voor {selectedPeriod.periodLabel}
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedPeriod(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* New Tickets */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#EA8162]" />
                  <span className="text-sm font-medium">
                    {selectedPeriod.created} nieuwe tickets
                  </span>
                  <Plus className="h-4 w-4 text-[#EA8162]" />
                </div>
                {selectedPeriod.createdTicketIds.length > 0 ? (
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {selectedPeriod.createdTicketIds.slice(0, 10).map((id) => (
                      <button
                        key={id}
                        onClick={() => openTicketInNewTab(id)}
                        className="flex items-center gap-2 text-xs text-primary hover:underline w-full text-left"
                      >
                        <ArrowSquareOut className="h-3 w-3" />
                        Ticket H{id}
                      </button>
                    ))}
                    {selectedPeriod.createdTicketIds.length > 10 && (
                      <button
                        onClick={() => openTicketsInHelpdeskView(selectedPeriod.createdTicketIds)}
                        className="text-xs text-primary hover:underline mt-1"
                      >
                        Bekijk alle {selectedPeriod.createdTicketIds.length} tickets in helpdesk
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Geen tickets in deze periode</p>
                )}
              </div>

              {/* Completed Tickets */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#5B7304]" />
                  <span className="text-sm font-medium">
                    {selectedPeriod.completed} opgeloste tickets
                  </span>
                  <CheckCircle className="h-4 w-4 text-[#5B7304]" />
                </div>
                {selectedPeriod.completedTicketIds.length > 0 ? (
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {selectedPeriod.completedTicketIds.slice(0, 10).map((id) => (
                      <button
                        key={id}
                        onClick={() => openTicketInNewTab(id)}
                        className="flex items-center gap-2 text-xs text-primary hover:underline w-full text-left"
                      >
                        <ArrowSquareOut className="h-3 w-3" />
                        Ticket H{id}
                      </button>
                    ))}
                    {selectedPeriod.completedTicketIds.length > 10 && (
                      <button
                        onClick={() => openTicketsInHelpdeskView(selectedPeriod.completedTicketIds)}
                        className="text-xs text-primary hover:underline mt-1"
                      >
                        Bekijk alle {selectedPeriod.completedTicketIds.length} tickets in helpdesk
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Geen tickets in deze periode</p>
                )}
              </div>
            </div>

            {/* Net Change */}
            <div className="mt-3 pt-3 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Netto wijziging:</span>
                <Badge
                  variant={
                    selectedPeriod.created - selectedPeriod.completed > 0
                      ? "destructive"
                      : selectedPeriod.created - selectedPeriod.completed < 0
                      ? "default"
                      : "secondary"
                  }
                >
                  {selectedPeriod.created - selectedPeriod.completed > 0 ? "+" : ""}
                  {selectedPeriod.created - selectedPeriod.completed} tickets
                </Badge>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
